import { useCallback } from 'react';

import type { UserConfig } from '@/entities/user-config';
import { useSaveUserConfig, useUserConfig, useUserDetails } from '@/features/_shared/user';
import { toPersistedMessage } from '@/features/ai-chat/lib/persistedMessage';
import saveUserHistory, { deriveConversationName } from '@/features/ai-chat/lib/saveHistory';
import { useChat } from '@/features/ai-chat/state/useChat';
import type { Conversation } from '@/features/ai-chat/types/chat.types';
import { clock } from '@/services/clock';

export const useRateAnswer = () => {
  const { messages, ratings, setRating, conversationId } = useChat();
  const { data: userConfigData } = useUserConfig();
  const { userEmail } = useUserDetails();
  const saveUserConfigMutation = useSaveUserConfig(userEmail ?? '');

  const handleRate = useCallback(
    (targetId: string, score: -1 | 1) => {
      setRating(targetId, score);
      if (!conversationId || !userConfigData) return;

      const nextRatings = { ...ratings, [targetId]: score };
      const persistedMessages = messages.map(message =>
        toPersistedMessage(message, nextRatings[message.id])
      );
      const timestamp = clock.date().toISOString();
      const existing = userConfigData.chatHistory as Conversation[];
      const idx = existing.findIndex(c => c.conversationId === conversationId);
      const updatedConversation = {
        conversationId,
        messages: persistedMessages,
        timestamp,
        name: deriveConversationName(persistedMessages)
      };
      saveUserHistory({
        userConfig: userConfigData,
        saveUser: (config: UserConfig) => {
          saveUserConfigMutation.mutate(config);
        },
        updatedConversation,
        existing,
        idx
      });
    },
    [conversationId, messages, ratings, setRating, userConfigData, saveUserConfigMutation]
  );

  return { handleRate, conversationId } as const;
};
